import { Collapse, Input, InputNumber, Select } from "antd";
import { isNil } from "lodash";
import { useTranslation } from "react-i18next";
import ConditionsEditor from "shared/components/ConditionsEditor";
import SourceEditor from "shared/components/SourceEditor";
import TransformsEditor from "shared/components/TransformsEditor";
import Button from "shared/components/Button";
import { NAME_SPACES } from "shared/locales/constants";
import { isDentalMapping, isStaticMapping } from "../../anchors";
import { EMPTY_CONDITIONS, EMPTY_RULE, MAPPING_TYPES } from "../../constants";
import { ZONE_TYPES } from "../../field-status";
import { withoutKey, zoneMappingKeys } from "./zones";

const FILL_TYPES = {
  TEXT: "text",
  STATIC: MAPPING_TYPES.STATIC,
  SPREAD: MAPPING_TYPES.SPREAD,
  DENTAL: MAPPING_TYPES.DENTAL,
};

const fillTypeOf = (mapping) => {
  if (isDentalMapping(mapping)) return FILL_TYPES.DENTAL;
  if (mapping?.type === FILL_TYPES.SPREAD) return FILL_TYPES.SPREAD;
  if (isStaticMapping(mapping)) return FILL_TYPES.STATIC;
  return FILL_TYPES.TEXT;
};

const switchFillType = (existing = {}, type) => {
  const { key, keys, from, combine, transforms } = existing;
  const source = { key, keys, from, combine, transforms };
  if (type === FILL_TYPES.SPREAD)
    return {
      ...source,
      type: FILL_TYPES.SPREAD,
      fontSize: existing.fontSize,
      start: existing.start,
      end: existing.end,
    };
  if (type === FILL_TYPES.DENTAL)
    return {
      ...source,
      type: FILL_TYPES.DENTAL,
      schema: existing.schema || "",
      fontSize: existing.fontSize,
    };
  if (type === FILL_TYPES.STATIC)
    return { type: FILL_TYPES.STATIC, staticValue: existing.staticValue || "" };
  return { ...source };
};

const ConfigField = ({ label, hint, children }) => (
  <div className="dynamic-documents-editor__config-field">
    <div className="dynamic-documents__config-field-label">{label}</div>
    {children}
    {hint && <div className="dynamic-documents__config-field-hint">{hint}</div>}
  </div>
);

const NumberField = ({ label, hint, value, onChange, ...rest }) => (
  <ConfigField label={label} hint={hint}>
    <InputNumber
      size="small"
      className="dynamic-documents__field-full"
      value={value ?? null}
      onChange={(next) => onChange(next ?? undefined)}
      {...rest}
    />
  </ConfigField>
);

const ColorField = ({ label, value, onChange }) => (
  <ConfigField label={label}>
    <div className="dynamic-documents__color-row">
      <input
        type="color"
        value={value || "#000000"}
        onChange={(event) => onChange(event.target.value)}
        className="dynamic-documents__color-input"
      />
      <span className="dynamic-documents__color-value">
        {value || "#000000"}
      </span>
    </div>
  </ConfigField>
);

const AdvancedOptions = ({ zone, mappings, onChange, onLeaveEmpty }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const ADVANCED = t("ADVANCED", { returnObjects: true });

  const setMapping = (key, next) =>
    onChange(
      isNil(next) ? withoutKey(mappings, key) : { ...mappings, [key]: next },
    );

  const setField = (key, field, value) =>
    setMapping(key, { ...(mappings[key] || {}), [field]: value });

  const conditionsProps = (key) => {
    const conditions = mappings[key]?.conditions || EMPTY_CONDITIONS;
    const commit = (next) => setField(key, "conditions", next);
    return {
      conditions,
      emptyMessage: ADVANCED.CONDITIONS_EMPTY,
      onOperatorChange: (operator) => commit({ ...conditions, operator }),
      onAddRule: () =>
        commit({
          ...conditions,
          rules: [...conditions.rules, EMPTY_RULE],
        }),
      onRemoveRule: (index) =>
        commit({
          ...conditions,
          rules: conditions.rules.filter((_, position) => position !== index),
        }),
      onUpdateRule: (index, patch) =>
        commit({
          ...conditions,
          rules: conditions.rules.map((rule, position) =>
            position === index ? { ...rule, ...patch } : rule,
          ),
        }),
    };
  };

  const sourceEditors = (key) => {
    const mapping = mappings[key] || {};
    return (
      <>
        <SourceEditor
          mapping={mapping}
          onChange={(next) => setMapping(key, next)}
        />
        <TransformsEditor
          transforms={mapping.transforms || []}
          onChange={(transforms) =>
            setField(
              key,
              "transforms",
              transforms.length > 0 ? transforms : undefined,
            )
          }
        />
      </>
    );
  };

  const anchorOptions = () => {
    const key = zone.key;
    const mapping = mappings[key] || {};
    const fillType = fillTypeOf(mapping);
    return (
      <>
        <ConfigField label={ADVANCED.FILL_TYPE}>
          <Select
            size="small"
            className="dynamic-documents__field-full"
            value={fillType}
            onChange={(type) => setMapping(key, switchFillType(mapping, type))}
            options={Object.values(FILL_TYPES).map((value) => ({
              value,
              label: ADVANCED.TYPES[value],
            }))}
          />
        </ConfigField>
        {fillType === FILL_TYPES.STATIC ? (
          <ConfigField label={ADVANCED.STATIC_VALUE}>
            <Input
              size="small"
              value={mapping.staticValue ?? ""}
              onChange={(event) =>
                setField(key, "staticValue", event.target.value)
              }
            />
          </ConfigField>
        ) : (
          sourceEditors(key)
        )}
        {fillType === FILL_TYPES.TEXT && (
          <>
            <NumberField
              label={ADVANCED.LETTER_SPACING}
              hint={ADVANCED.LETTER_SPACING_HINT}
              min={-20}
              max={40}
              step={1}
              precision={1}
              placeholder="0"
              addonAfter="px"
              value={mapping.letterSpacing}
              onChange={(value) => setField(key, "letterSpacing", value)}
            />
            <NumberField
              label={ADVANCED.START}
              hint={ADVANCED.START_HINT}
              min={0}
              precision={0}
              placeholder="0"
              value={mapping.start}
              onChange={(value) => setField(key, "start", value)}
            />
            <NumberField
              label={ADVANCED.END}
              hint={ADVANCED.END_HINT}
              min={1}
              precision={0}
              placeholder={ADVANCED.INFINITY}
              value={mapping.end}
              onChange={(value) => setField(key, "end", value)}
            />
          </>
        )}
        {fillType === FILL_TYPES.SPREAD && (
          <>
            <NumberField
              label={ADVANCED.FONT_SIZE}
              min={4}
              max={72}
              precision={1}
              placeholder="9"
              addonAfter="pt"
              value={mapping.fontSize}
              onChange={(value) => setField(key, "fontSize", value)}
            />
            <NumberField
              label={ADVANCED.SPREAD_START}
              min={0}
              precision={0}
              placeholder="0"
              value={mapping.start}
              onChange={(value) => setField(key, "start", value)}
            />
            <NumberField
              label={ADVANCED.SPREAD_END}
              min={1}
              precision={0}
              placeholder={ADVANCED.INFINITY}
              value={mapping.end}
              onChange={(value) => setField(key, "end", value)}
            />
          </>
        )}
        {fillType === FILL_TYPES.DENTAL && (
          <>
            <NumberField
              label={ADVANCED.FONT_SIZE}
              min={4}
              max={72}
              precision={1}
              placeholder="9"
              addonAfter="pt"
              value={mapping.fontSize}
              onChange={(value) => setField(key, "fontSize", value)}
            />
            <ConfigField label={ADVANCED.SCHEMA} hint={ADVANCED.SCHEMA_HINT}>
              <Input
                size="small"
                className="dynamic-documents__field-mono"
                placeholder={ADVANCED.SCHEMA_PLACEHOLDER}
                value={mapping.schema ?? ""}
                onChange={(event) =>
                  setField(key, "schema", event.target.value || undefined)
                }
              />
            </ConfigField>
          </>
        )}
        <ConditionsEditor {...conditionsProps(key)} />
        <Button type="gray--outlined" onClick={onLeaveEmpty}>
          {ADVANCED.LEAVE_EMPTY}
        </Button>
      </>
    );
  };

  const childLoopOptions = () => (
    <>
      {sourceEditors(zone.key)}
      <ConfigField label={ADVANCED.ITEM_KEY} hint={ADVANCED.ITEM_KEY_HINT}>
        <Input
          size="small"
          className="dynamic-documents__field-mono"
          placeholder={ADVANCED.ITEM_KEY_PLACEHOLDER}
          value={mappings[zone.key]?.itemKey ?? ""}
          onChange={(event) =>
            setField(zone.key, "itemKey", event.target.value || undefined)
          }
        />
      </ConfigField>
      <ConditionsEditor {...conditionsProps(zone.key)} />
    </>
  );

  const checkboxOptions = () =>
    zone.anchors.map((anchor) => {
      const mapping = mappings[anchor] || {};
      return (
        <div key={anchor} className="dynamic-documents-editor__advanced-box">
          <div className="dynamic-documents-editor__advanced-box-title">
            {t("ADVANCED.BOX", { name: anchor })}
          </div>
          <NumberField
            label={ADVANCED.WIDTH}
            min={1}
            precision={0}
            placeholder="10"
            addonAfter="px"
            value={mapping.width}
            onChange={(value) => setField(anchor, "width", value)}
          />
          <NumberField
            label={ADVANCED.HEIGHT}
            min={1}
            precision={0}
            placeholder="10"
            addonAfter="px"
            value={mapping.height}
            onChange={(value) => setField(anchor, "height", value)}
          />
          <ColorField
            label={ADVANCED.FILL_COLOR}
            value={mapping.color}
            onChange={(value) => setField(anchor, "color", value)}
          />
          <ConditionsEditor {...conditionsProps(anchor)} />
        </div>
      );
    });

  const OPTIONS = {
    [ZONE_TYPES.ANCHOR]: anchorOptions,
    [ZONE_TYPES.CHILD_LOOP]: childLoopOptions,
    [ZONE_TYPES.CHECKBOX_GROUP]: checkboxOptions,
  };

  const technical = Object.fromEntries(
    zoneMappingKeys(zone).map((key) => [key, mappings[key] ?? null]),
  );

  return (
    <Collapse
      ghost
      className="dynamic-documents-editor__advanced"
      items={[
        {
          key: "advanced",
          label: ADVANCED.TITLE,
          children: (
            <div className="dynamic-documents-editor__advanced-body">
              {OPTIONS[zone.type]?.()}
              <Collapse
                ghost
                size="small"
                items={[
                  {
                    key: "technical",
                    label: ADVANCED.TECHNICAL,
                    children: (
                      <div className="dynamic-documents-editor__technical">
                        <div className="dynamic-documents__config-field-label">
                          {ADVANCED.ZONE_NAME}
                        </div>
                        <code>{zone.id}</code>
                        <div className="dynamic-documents__config-field-label">
                          {ADVANCED.RAW}
                        </div>
                        <pre>{JSON.stringify(technical, null, 2)}</pre>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          ),
        },
      ]}
    />
  );
};

export default AdvancedOptions;
